import React, { useState, useRef, useEffect } from 'react';
import { Lock, X, ShieldCheck } from 'lucide-react';

// SHA-256 in the browser via SubtleCrypto. We never store the raw PIN.
async function sha256(text) {
  const buf = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

const PIN_KEY = 'cropasaurus_lab_pin_sha256';

export default function PinGate({ onUnlock, onClose }) {
  const isFirstTime = !localStorage.getItem(PIN_KEY);
  const [mode, setMode] = useState(isFirstTime ? 'set' : 'enter');
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const firstInput = useRef(null);

  useEffect(() => {
    firstInput.current?.focus();
  }, [mode]);

  async function handleSubmit(e) {
    e?.preventDefault();
    setErr('');
    if (busy) return;
    if (mode === 'set') {
      if (pin.length < 4) { setErr('PIN must be at least 4 digits'); return; }
      if (pin !== pinConfirm) { setErr('PINs do not match'); return; }
      setBusy(true);
      const hash = await sha256(pin);
      localStorage.setItem(PIN_KEY, hash);
      setBusy(false);
      onUnlock();
    } else {
      setBusy(true);
      const hash = await sha256(pin);
      const stored = localStorage.getItem(PIN_KEY);
      setBusy(false);
      if (hash === stored) onUnlock();
      else { setErr('Wrong PIN'); setPin(''); firstInput.current?.focus(); }
    }
  }

  function reset() {
    if (window.confirm('Forgetting the PIN will wipe the lock — you can set a new one. Continue?')) {
      localStorage.removeItem(PIN_KEY);
      setMode('set'); setPin(''); setPinConfirm(''); setErr('');
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-center justify-center p-5"
      style={{ background: 'rgba(31,61,43,0.85)' }}
      data-testid="pin-gate"
    >
      <form
        onSubmit={handleSubmit}
        className="border-2 p-6 md:p-8 w-full max-w-md relative"
        style={{ background: 'var(--cream)', borderColor: 'var(--ink)', color: 'var(--ink)' }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1"
          style={{ color: 'var(--ink)' }}
          data-testid="pin-close"
          aria-label="Close PIN gate"
        >
          <X size={20} />
        </button>

        <div className="flex items-center gap-3 mb-2">
          {mode === 'set' ? <ShieldCheck size={22} /> : <Lock size={22} />}
          <h2 className="font-display text-xl md:text-2xl tracking-wider">
            {mode === 'set' ? 'SET LAB PIN' : 'ENTER LAB PIN'}
          </h2>
        </div>
        <p className="font-mono text-[11px] tracking-widest mb-5 opacity-70">
          {mode === 'set'
            ? 'PIN IS HASHED LOCALLY ON THIS DEVICE. NEVER LEAVES THE BROWSER.'
            : 'PRIVATE FLOWER LAB · OWNER ONLY'}
        </p>

        <label className="block mb-3">
          <span className="font-mono text-[10px] tracking-widest opacity-60">PIN</span>
          <input
            ref={firstInput}
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, '').slice(0, 12))}
            className="input-rex"
            data-testid="pin-input"
            placeholder="••••"
          />
        </label>

        {mode === 'set' && (
          <label className="block mb-3">
            <span className="font-mono text-[10px] tracking-widest opacity-60">CONFIRM PIN</span>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={pinConfirm}
              onChange={(e) => setPinConfirm(e.target.value.replace(/[^0-9]/g, '').slice(0, 12))}
              className="input-rex"
              data-testid="pin-confirm"
              placeholder="••••"
            />
          </label>
        )}

        {err && (
          <p className="font-mono text-[11px] mb-3" style={{ color: '#b91c1c' }} data-testid="pin-error">
            {err}
          </p>
        )}

        <button
          type="submit"
          className="btn-amber w-full inline-flex items-center justify-center gap-2"
          disabled={busy}
          data-testid="pin-submit"
        >
          <Lock size={16} />
          {busy ? 'CHECKING…' : (mode === 'set' ? 'SET PIN & ENTER LAB' : 'UNLOCK LAB')}
        </button>

        {mode === 'enter' && (
          <button
            type="button"
            onClick={reset}
            className="mt-4 font-mono text-[10px] tracking-widest opacity-50 underline w-full"
            data-testid="pin-forgot"
          >
            FORGOT PIN — RESET
          </button>
        )}
      </form>
    </div>
  );
}
